"use client";
// LEGACY UI: replace with a toast primitive (frontend-ui has none yet: add shadcn Sonner) from @workspace/frontend-ui.
// Don't import this in new code; legacy-ui.json tracks the remaining importers
// and only shrinks (scripts/legacy-ui-check.mjs).

import classNames from "@casino/external/classNames";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

type Position =
  | "top-left"
  | "top-center"
  | "top-right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right";

type RootProps = {
  isOpen: boolean;
  onOpenChange?: (open: boolean) => void;
  position?: Position;
  autoClose?: number;
  className?: string;
  children?: React.ReactNode;
};

const positionClasses: Record<Position, string> = {
  "top-left": "top-4 start-4",
  "top-center": "top-4 inset-x-4 md:mx-auto md:w-fit",
  "top-right": "top-4 end-4",
  "bottom-left": "bottom-4 start-4",
  "bottom-center": "bottom-4 inset-x-4 mx-auto w-fit",
  "bottom-right": "bottom-4 end-4",
};

const SnackbarRoot = ({
  isOpen,
  onOpenChange,
  position = "top-right",
  autoClose,
  className,
  children,
}: RootProps) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (isOpen && autoClose) {
      timerRef.current = setTimeout(() => {
        onOpenChange?.(false);
      }, autoClose);
    }
    return () => clearTimeout(timerRef.current);
  }, [isOpen, autoClose, onOpenChange]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className={classNames(
        "z-50 flex fixed w-[calc(100%-32px)] md:w-fit transition",
        positionClasses[position],
      )}
      role="status"
      aria-live="polite"
    >
      <div
        className={classNames(
          "flex w-fit max-w-xs items-center gap-4 p-4 bg-background shadow-lg rounded-lg",
          className,
        )}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
};

type IconProps = { children?: React.ReactNode; className?: string };

const Icon = ({ children, className }: IconProps) => (
  <div
    aria-hidden="true"
    className={classNames(
      "flex h-8 aspect-square items-center justify-center rounded-lg text-display-xs",
      className,
    )}
  >
    {children}
  </div>
);

type MessageProps = { children?: React.ReactNode; className?: string };

const Message = ({ children, className }: MessageProps) => (
  <p
    className={classNames(
      "w-full text-sm/6 transition-colors text-foreground",
      className,
    )}
  >
    {children}
  </p>
);

type HeaderProps = { children?: React.ReactNode; className?: string };

const Header = ({ children, className }: HeaderProps) => (
  <p
    className={classNames(
      "w-full text-sm/6 font-medium transition-colors text-foreground",
      className,
    )}
  >
    {children}
  </p>
);

type ContentProps = { children?: React.ReactNode; className?: string };

const Content = ({ children, className }: ContentProps) => (
  <div className={classNames("w-full", className)}>{children}</div>
);

type CloseProps = {
  children?: React.ReactNode;
  className?: string;
  ariaLabel?: string;
  onClick?: () => void;
};

const Close = ({
  children,
  className,
  ariaLabel = "Close",
  onClick,
}: CloseProps) => (
  <button
    type="button"
    aria-label={ariaLabel}
    onClick={onClick}
    className={classNames(
      "flex h-8 aspect-square items-center justify-center text-foreground text-2xl",
      className,
    )}
  >
    {children}
  </button>
);

const Snackbar = Object.assign(SnackbarRoot, {
  Icon,
  Message,
  Header,
  Content,
  Close,
});

export default Snackbar;
